import { z } from 'zod';
import {
  countryCodeSchema,
  entityIdSchema,
  hexColourSchema,
  localeSchema,
  safeTextSchema,
  uniqueArraySchema,
} from './common';

/**
 * A participating member broadcaster configuration (white-label instance). Pilot instances are
 * fictional (`markStatus: 'fictional'`); official marks require `authorised` plus real permission.
 */

export const BROADCASTER_MODULES = [
  'catalogue',
  'watch',
  'discovery-game',
  'world-builder',
  'sample-learner',
] as const;
export const broadcasterModuleSchema = z.enum(BROADCASTER_MODULES);
export type BroadcasterModule = z.infer<typeof broadcasterModuleSchema>;

export const MARK_STATUSES = ['fictional', 'authorised'] as const;

export const broadcasterThemeSchema = z.strictObject({
  /** Local accent colour; `accentText` must reach WCAG AA contrast on it. */
  accent: hexColourSchema,
  accentText: hexColourSchema,
  /** Short label shown with the accent, e.g. "Demo · Estonia". */
  label: safeTextSchema(40),
});

export const broadcasterSchema = z
  .strictObject({
    id: entityIdSchema,
    displayName: safeTextSchema(80),
    country: countryCodeSchema,
    supportedLocales: uniqueArraySchema(localeSchema).min(1).max(4),
    defaultLocale: localeSchema,
    theme: broadcasterThemeSchema,
    markStatus: z.enum(MARK_STATUSES),
    enabledModules: uniqueArraySchema(broadcasterModuleSchema),
  })
  .refine((b) => b.supportedLocales.includes(b.defaultLocale), {
    message: 'defaultLocale must be one of supportedLocales',
    path: ['defaultLocale'],
  });
export type Broadcaster = z.infer<typeof broadcasterSchema>;

/** Editorial curation: which items a broadcaster features, in deliberate order. */
export const curationEntrySchema = z.strictObject({
  broadcasterId: entityIdSchema,
  contentItemId: entityIdSchema,
  position: z.int().min(0).max(10_000),
});
export type CurationEntry = z.infer<typeof curationEntrySchema>;
